import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  ShieldOff, UserX, Ban, Trash2, RotateCcw, Building2, UserCog,
  UserCircle, HeartHandshake, ExternalLink, Clock, RefreshCw,
} from "lucide-react";

/**
 * Painel de Governança de Restrições.
 *
 * Mostra tudo que o admin precisa pra saber "quem está fora do sistema" e por quê:
 * - Usuários com login bloqueado (todas as roles)
 * - Profissionais reprovados no credenciamento
 * - Candidaturas reprovadas
 * - Últimas exclusões (audit log 90d)
 *
 * Restaurar acesso (desbloquear) fica só nesta tela — a intenção é forçar
 * o admin a passar por esta visão consolidada antes de reativar alguém.
 */

const ROLE_META = {
  admin:        { label: "Admin",            icon: ShieldOff,      bg: "bg-slate-900 text-white" },
  consultor:    { label: "Consultor",        icon: UserCog,        bg: "bg-indigo-100 text-indigo-800" },
  profissional: { label: "Profissional",     icon: HeartHandshake, bg: "bg-emerald-100 text-emerald-800" },
  clinica:      { label: "Unidade Parceira", icon: Building2,      bg: "bg-amber-100 text-amber-800" },
  familia:      { label: "Família",          icon: UserCircle,     bg: "bg-slate-100 text-slate-700" },
};

const DELETION_META = {
  professional_deleted: { label: "Profissional excluído", color: "bg-red-100 text-red-800 border-red-200" },
  admin_user_deleted:   { label: "Usuário excluído",       color: "bg-red-100 text-red-800 border-red-200" },
  clinic_removed:       { label: "Clínica removida",       color: "bg-amber-100 text-amber-800 border-amber-200" },
  user_deleted:         { label: "Usuário excluído",       color: "bg-red-100 text-red-800 border-red-200" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR"); } catch { return iso; }
};

const BannedUsersPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState("all");

  const load = async () => {
    try {
      const r = await api.get("/admin/banned");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao carregar banidos");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const restore = async (u) => {
    if (!window.confirm(`Restaurar acesso de ${u.name || u.email}?`)) return;
    try {
      await api.post(`/admin/banned/${u.id}/restore`);
      toast.success("Acesso restaurado");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar");
    }
  };

  if (loading) return <div className="p-10 text-slate-500 text-center" data-testid="banned-loading">Carregando registros de restrição…</div>;
  if (!data) return null;

  const allBlocked = Object.values(data.blocked_by_role).flat();
  const blockedFiltered = roleFilter === "all"
    ? allBlocked
    : (data.blocked_by_role[roleFilter] || []);

  return (
    <div className="space-y-6" data-testid="banned-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display font-bold text-2xl text-[#0B2B62] inline-flex items-center gap-2">
            <ShieldOff className="w-6 h-6 text-red-600" /> Banidos, Bloqueados & Excluídos
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            Governança consolidada de restrições — quem perdeu acesso, quando e por quê.
          </p>
        </div>
        <button onClick={load} data-testid="banned-refresh"
          className="text-xs px-3 py-1.5 border border-slate-200 rounded-md hover:bg-slate-50 inline-flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Ban}     label="Login bloqueado"           value={data.blocked_total}                          color="bg-red-50 text-red-700 border-red-200" />
        <KPI icon={UserX}   label="Profissionais reprovados" value={data.rejected_professionals.length}          color="bg-orange-50 text-orange-700 border-orange-200" />
        <KPI icon={UserX}   label="Candidaturas reprovadas"  value={data.rejected_applications.length}           color="bg-amber-50 text-amber-800 border-amber-200" />
        <KPI icon={Trash2}  label="Exclusões (90d)"          value={data.recent_deletions.length}                color="bg-slate-100 text-slate-700 border-slate-300" />
      </div>

      {/* Filtro por role (aplicável só à seção Bloqueados) */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="banned-blocked-section">
        <header className="p-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <h3 className="font-display font-bold text-[#0B2B62] inline-flex items-center gap-2">
            <Ban className="w-4 h-4 text-red-600" /> Usuários com login bloqueado ({allBlocked.length})
          </h3>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setRoleFilter("all")} data-testid="banned-filter-all"
              className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border font-semibold ${roleFilter === "all" ? "bg-[#0B2B62] text-white border-[#0B2B62]" : "bg-white text-slate-600 border-slate-300 hover:border-[#0B2B62]"}`}>
              Todos ({allBlocked.length})
            </button>
            {Object.entries(data.blocked_by_role).map(([role, arr]) => {
              const meta = ROLE_META[role] || { label: role, icon: UserCircle };
              const Icon = meta.icon;
              return (
                <button key={role} onClick={() => setRoleFilter(role)}
                  data-testid={`banned-filter-${role}`}
                  className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border font-semibold ${roleFilter === role ? "bg-[#0B2B62] text-white border-[#0B2B62]" : `${meta.bg} border-transparent hover:brightness-95`}`}>
                  <Icon className="w-3 h-3" /> {meta.label} ({arr.length})
                </button>
              );
            })}
          </div>
        </header>
        {blockedFiltered.length === 0 ? (
          <div className="p-10 text-center text-slate-500 text-sm">
            <Ban className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            {roleFilter === "all" ? "Nenhum usuário bloqueado 🎉" : `Nenhum ${ROLE_META[roleFilter]?.label || roleFilter} bloqueado.`}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[820px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Perfil</th>
                <th className="px-4 py-2 text-left">Nome</th>
                <th className="px-4 py-2 text-left">E-mail</th>
                <th className="px-4 py-2 text-left">Telefone</th>
                <th className="px-4 py-2 text-left">Bloqueado em</th>
                <th className="px-4 py-2 text-right">Ação</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {blockedFiltered.map((u) => {
                const meta = ROLE_META[u.role] || { label: u.role, icon: UserCircle, bg: "bg-slate-100" };
                const Icon = meta.icon;
                return (
                  <tr key={u.id} className="hover:bg-slate-50" data-testid={`banned-user-${u.id}`}>
                    <td className="px-4 py-2.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-widest ${meta.bg} px-2 py-0.5 rounded-full font-semibold`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 font-medium text-slate-800">{u.display_name || u.name}</td>
                    <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{u.email}</td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{u.phone || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{formatDate(u.updated_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => restore(u)} data-testid={`banned-restore-${u.id}`}
                        className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-md bg-emerald-700 hover:bg-emerald-800 text-white font-semibold">
                        <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {/* Profissionais reprovados */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="banned-rejected-profs">
        <header className="p-4 border-b border-slate-100 inline-flex items-center gap-2">
          <UserX className="w-4 h-4 text-orange-600" />
          <h3 className="font-display font-bold text-[#0B2B62]">Profissionais reprovados no credenciamento ({data.rejected_professionals.length})</h3>
        </header>
        {data.rejected_professionals.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Nenhum profissional reprovado.</div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {data.rejected_professionals.map((p) => (
              <li key={p.id} className="p-3 hover:bg-slate-50 text-sm" data-testid={`rejected-prof-${p.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-semibold text-slate-800">{p.display_name || p.name}</div>
                    <div className="text-xs text-slate-500 font-mono">{p.email} · {p.phone || "sem telefone"}</div>
                  </div>
                  <div className="text-xs text-slate-500">{formatDate(p.updated_at)}</div>
                </div>
                {p.rejection_reason && (
                  <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded-md">
                    <b>Motivo:</b> {p.rejection_reason}
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Candidaturas reprovadas */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="banned-rejected-apps">
        <header className="p-4 border-b border-slate-100 inline-flex items-center gap-2">
          <UserX className="w-4 h-4 text-amber-600" />
          <h3 className="font-display font-bold text-[#0B2B62]">Candidaturas reprovadas ({data.rejected_applications.length})</h3>
        </header>
        {data.rejected_applications.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Nenhuma candidatura reprovada.</div>
        ) : (
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left">Tipo</th>
                <th className="px-4 py-2 text-left">Nome</th>
                <th className="px-4 py-2 text-left">Contato</th>
                <th className="px-4 py-2 text-left">Motivo</th>
                <th className="px-4 py-2 text-left">Reprovada em</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.rejected_applications.map((a) => (
                <tr key={a.id} className="hover:bg-slate-50" data-testid={`rejected-app-${a.id}`}>
                  <td className="px-4 py-2.5">
                    <span className="text-[10px] uppercase tracking-widest bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full font-semibold">{a.type}</span>
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{a.name}</td>
                  <td className="px-4 py-2.5 text-slate-600 text-xs">
                    <div className="font-mono">{a.email}</div>
                    <div>{a.phone || "—"}</div>
                  </td>
                  <td className="px-4 py-2.5 text-xs text-red-700 max-w-xs">{a.reason || "—"}</td>
                  <td className="px-4 py-2.5 text-slate-500 text-xs">{formatDate(a.decided_at || a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {/* Exclusões recentes (auditoria) */}
      <section className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="banned-deletions">
        <header className="p-4 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          <h3 className="font-display font-bold text-[#0B2B62] inline-flex items-center gap-2">
            <Trash2 className="w-4 h-4 text-red-600" />
            Exclusões definitivas (últimos 90 dias · {data.recent_deletions.length})
          </h3>
          <span className="text-[10px] uppercase tracking-widest bg-red-100 text-red-800 border border-red-200 px-2 py-1 rounded-full font-bold">
            Irreversível
          </span>
        </header>
        <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 text-[11px] text-amber-900 leading-relaxed">
          <b>Como funciona:</b> exclusão apaga o registro do banco. O admin <b>não</b> consegue reverter pelo painel.
          O e-mail volta a ficar livre — a pessoa pode se cadastrar de novo, mas vira uma conta nova sem histórico.
          Para reativar sem perder histórico, prefira <b>Bloquear</b> em vez de Excluir.
        </div>
        {data.recent_deletions.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">Nenhuma exclusão registrada nos últimos 90 dias.</div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[500px] overflow-y-auto">
            {data.recent_deletions.map((d, i) => {
              const meta = DELETION_META[d.action] || { label: d.action, color: "bg-slate-100 text-slate-700 border-slate-200" };
              return (
                <li key={i} className="p-3 hover:bg-slate-50 text-sm" data-testid={`deletion-${i}`}>
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <span className={`text-[10px] uppercase tracking-widest ${meta.color} border px-2 py-0.5 rounded-full font-semibold`}>
                        {meta.label}
                      </span>
                      <div className="mt-1 text-xs">
                        <span className="text-slate-500">Alvo:</span> <span className="font-mono text-slate-700">{d.target_id?.slice(0, 12) || "—"}…</span>
                        {d.meta?.email && <> · <span className="font-mono text-slate-600">{d.meta.email}</span></>}
                        {d.meta?.name && <> · <b className="text-slate-800">{d.meta.name}</b></>}
                      </div>
                      {d.actor_email && (
                        <div className="text-[10px] text-slate-500 mt-0.5">
                          Executado por: <span className="font-mono">{d.actor_email}</span>
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 inline-flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {formatDate(d.at)}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
};

const KPI = ({ icon: Icon, label, value, color }) => (
  <div className={`p-4 border rounded-xl ${color}`}>
    <div className="flex items-center gap-2">
      <Icon className="w-4 h-4" />
      <div className="text-[10px] uppercase tracking-widest font-semibold">{label}</div>
    </div>
    <div className="font-display font-bold text-3xl mt-1">{value}</div>
  </div>
);

export default BannedUsersPanel;
